import { FC } from "react";
import { Box, Stack, Typography, LinearProgress, Alert, CircularProgress } from "@mui/material";
import { CheckCircle, CloudDownload, Error as ErrorIcon } from "@mui/icons-material";

import { ActionModal } from "@/components";
import { tokens } from "@/tokens";

interface ReconciliationProgressModalProps {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  onDownload?: (url: string) => void;
  onClose: () => void;
  fileId?: string;
  status?: 'PENDING' | 'SUCCESS' | 'FAILED';
  progress?: number;
  downloadUrl?: string;
  error?: string;
  adjustmentType?: string;
}

const formatAdjustmentType = (type?: string): string => {
  switch (type) {
    case 'value_percentage':
      return 'Value Adjustment';
    case 'duty_rate_adjustment':
      return 'Duty Rate Adjustment';
    case 'combined_value_and_duty':
      return 'Combined Value & Duty Adjustment';
    case 'line_specific':
      return 'Line-Specific Adjustment';
    default:
      return 'Adjustment';
  }
};

const ReconciliationProgressModal: FC<ReconciliationProgressModalProps> = ({
  show,
  setShow,
  onDownload,
  onClose,
  fileId,
  status = 'PENDING',
  progress = 0,
  downloadUrl,
  error,
  adjustmentType = 'adjustment',
}) => {
  const formattedAdjustmentType = formatAdjustmentType(adjustmentType);

  const getStatusIcon = () => {
    switch (status) {
      case 'SUCCESS':
        return <CheckCircle sx={{ fontSize: 48, color: tokens.status.success }} />;
      case 'FAILED':
        return <ErrorIcon sx={{ fontSize: 48, color: tokens.status.danger }} />;
      default:
        return <CircularProgress size={48} />;
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'SUCCESS':
        return 'Processing Complete';
      case 'FAILED':
        return 'Processing Failed';
      default:
        return 'Processing Adjustment...';
    }
  };

  const getStatusMessage = () => {
    switch (status) {
      case 'SUCCESS':
        return 'Your adjustment has been processed successfully and is ready for download.';
      case 'FAILED':
        return error || 'An error occurred while processing the adjustment. Please try again.';
      default:
        return 'Please wait while we process your adjustment data. This may take several minutes for large datasets.';
    }
  };

  const handleDownload = () => {
    if (downloadUrl && onDownload) {
      onDownload(downloadUrl);
    }
  };

  const handleClose = () => {
    onClose();
    setShow(false);
  };

  const getButtonText = () => {
    if (status === 'SUCCESS' && downloadUrl) {
      return 'Download Results';
    }
    if (status === 'FAILED') {
      return 'Close';
    }
    return 'Processing...'; // Default text during processing
  };

  const getButtonAction = () => {
    if (status === 'SUCCESS' && downloadUrl) {
      return handleDownload;
    }
    if (status === 'FAILED') {
      return handleClose;
    }
    return () => {}; // Empty function during processing
  };

  return (
    <ActionModal
      show={show}
      setShow={(show) => setShow(show)}
      buttonText={getButtonText()}
      buttonAction={getButtonAction()}
      title="Processing Adjustment"
      maxWidth="sm"
      hideActionButton={status === 'PENDING'}
    >
      <Stack spacing={4} alignItems="center" py={2}>
        {/* Status Icon */}
        <Box>
          {getStatusIcon()}
        </Box>

        {/* Status Text */}
        <Stack spacing={1} alignItems="center" textAlign="center">
          <Typography variant="h6" sx={{ color: status === 'FAILED' ? tokens.status.danger : tokens.text.primary }}>
            {getStatusText()}
          </Typography>
          <Typography variant="body2" sx={{ color: tokens.text.secondary }} maxWidth="400px">
            {getStatusMessage()}
          </Typography>
        </Stack>

        {/* Progress Bar (only during processing) */}
        {status === 'PENDING' && (
          <Box width="100%" maxWidth="400px">
            <Stack spacing={2}>
              <LinearProgress 
                variant={progress > 0 ? 'determinate' : 'indeterminate'} 
                value={progress}
                sx={{ height: 8, borderRadius: 4 }}
              />
              {progress > 0 && (
                <Typography variant="body2" sx={{ color: tokens.text.secondary }} textAlign="center">
                  {Math.round(progress)}% Complete
                </Typography>
              )}
            </Stack>
          </Box>
        )}

        {/* File ID for tracking */}
        {fileId && status === 'PENDING' && (
          <Box>
            <Typography variant="caption" sx={{ color: tokens.text.secondary }} fontFamily="monospace">
              Processing ID: {fileId}
            </Typography>
          </Box>
        )}

        {/* Success Alert */}
        {status === 'SUCCESS' && (
          <Alert severity="success" sx={{ width: '100%' }}>
            <Stack spacing={1}>
              <Typography variant="body2">
                <strong>Success!</strong> Your {formattedAdjustmentType} processing is complete.
              </Typography>
              {downloadUrl && (
                <Typography variant="body2">
                  Click "Download Results" to get your processed data file.
                </Typography>
              )}
            </Stack>
          </Alert>
        )}

        {/* Error Alert */}
        {status === 'FAILED' && (
          <Alert severity="error" sx={{ width: '100%' }}>
            <Stack spacing={1}>
              <Typography variant="body2">
                <strong>Processing Failed</strong>
              </Typography>
              {error && (
                <Typography variant="body2">
                  {error}
                </Typography>
              )}
              <Typography variant="body2">
                Please check your configuration and try again. If the problem persists, contact support.
              </Typography>
            </Stack>
          </Alert>
        )}

        {/* Download Icon for Success State */}
        {status === 'SUCCESS' && downloadUrl && (
          <Box
            onClick={handleDownload}
            sx={{
              cursor: 'pointer',
              p: 2,
              borderRadius: 2,
              border: '2px dashed',
              borderColor: tokens.status.success,
              bgcolor: tokens.status.successBg,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              '&:hover': {
                bgcolor: tokens.status.success,
                color: 'white',
              },
              transition: 'all 0.2s',
            }}
          >
            <CloudDownload />
            <Typography variant="body2" fontWeight="medium">
              Click to Download Results
            </Typography>
          </Box>
        )}

        {/* Processing Details */}
        {status === 'PENDING' && (
          <Box sx={{ width: '100%', textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: tokens.text.secondary }}>
              Processing steps:
            </Typography>
            <Stack spacing={0.5} mt={1}>
              <Typography variant="caption" sx={{ color: tokens.text.secondary }}>
                ✓ Configuration validated
              </Typography>
              <Typography variant="caption" sx={{ color: tokens.text.secondary }}>
                ✓ Data scope analyzed
              </Typography>
              <Typography variant="caption" sx={{ color: progress > 30 ? tokens.text.primary : tokens.text.secondary }}>
                {progress > 30 ? '✓' : '⋯'} Applying adjustments
              </Typography>
              <Typography variant="caption" sx={{ color: progress > 80 ? tokens.text.primary : tokens.text.secondary }}>
                {progress > 80 ? '✓' : '⋯'} Generating report
              </Typography>
              <Typography variant="caption" sx={{ color: progress >= 100 ? tokens.text.primary : tokens.text.secondary }}>
                {progress >= 100 ? '✓' : '⋯'} Finalizing results
              </Typography>
            </Stack>
          </Box>
        )}
      </Stack>
    </ActionModal>
  );
};

export default ReconciliationProgressModal;